/**
 * Attach Document Dialog
 * Shown from TripCard — lets user pick from their unlinked documents to attach to a trip
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Loader2, Search, X, Paperclip } from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttachDocumentDialog = ({ trip, onSuccess, onClose }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attaching, setAttaching] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/documents`);
        // Show all docs — unlinked first, then already-linked ones greyed out
        setDocuments(response.data.documents || []);
      } catch (err) {
        toast.error('Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
  }, []);

  const filtered = documents.filter(d => {
    const q = search.toLowerCase();
    return (
      (d.file_name || '').toLowerCase().includes(q) ||
      (d.document_type || '').toLowerCase().includes(q)
    );
  });

  // Sort: unlinked first, then linked to this trip, then linked to other trips
  const sorted = [...filtered].sort((a, b) => {
    const aLinked = a.linked_trip_id === trip.id ? 1 : a.linked_trip_id ? 2 : 0;
    const bLinked = b.linked_trip_id === trip.id ? 1 : b.linked_trip_id ? 2 : 0;
    return aLinked - bLinked;
  });

  const handleAttach = async (docId) => {
    setAttaching(true);
    try {
      await axios.put(`${API_URL}/api/documents/${docId}/link`, { trip_id: trip.id });
      toast.success('Document attached to trip');
      onSuccess?.();
    } catch (err) {
      toast.error('Failed to attach document');
    } finally {
      setAttaching(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }); } catch (err) { return d; }
  };

  return (
    <DialogContent className="bg-slate-800 border-slate-700 max-w-lg max-h-[80vh] flex flex-col" data-testid="attach-doc-dialog">
      <DialogHeader>
        <DialogTitle className="text-white flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-amber-400" />
          Attach Document
        </DialogTitle>
        <p className="text-xs text-slate-400 mt-1">
          {trip.vessel_name || trip.trip_name || 'Trip'}
          {trip.date_joined_vessel && ` — ${formatDate(trip.date_joined_vessel)}`}
        </p>
      </DialogHeader>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-slate-900/50 border-slate-600 text-white pl-10 h-9"
          data-testid="doc-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Document list */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0 max-h-[40vh]">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 text-amber-500 animate-spin" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-center text-slate-500 py-8 text-sm">No documents found</p>
        ) : (
          sorted.map(doc => {
            const isLinkedHere = doc.linked_trip_id === trip.id;
            const isLinkedElsewhere = doc.linked_trip_id && doc.linked_trip_id !== trip.id;
            return (
              <button
                key={doc.id}
                onClick={() => !isLinkedHere && !isLinkedElsewhere && handleAttach(doc.id)}
                disabled={attaching || isLinkedHere || isLinkedElsewhere}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  isLinkedHere
                    ? 'bg-amber-600/10 border border-amber-500/30 cursor-default'
                    : isLinkedElsewhere
                    ? 'bg-slate-900/20 border border-slate-700/30 opacity-50 cursor-not-allowed'
                    : 'bg-slate-900/30 border border-slate-700/50 hover:border-amber-500/30 hover:bg-slate-700/50'
                }`}
                data-testid={`attach-doc-${doc.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className={`h-4 w-4 shrink-0 ${isLinkedHere ? 'text-amber-400' : 'text-blue-400'}`} />
                    <span className={`font-medium truncate text-sm ${isLinkedHere ? 'text-amber-300' : 'text-white'}`}>
                      {doc.file_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge className="bg-slate-700/50 text-slate-400 border-slate-600 text-[10px]">
                      {(doc.document_type || 'other').replace(/_/g, ' ')}
                    </Badge>
                    {isLinkedHere && (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">Attached</Badge>
                    )}
                    {isLinkedElsewhere && (
                      <Badge className="bg-slate-600/50 text-slate-500 border-slate-600 text-[10px]">Linked elsewhere</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 pl-6">
                  {doc.tax_year && <span>{doc.tax_year}</span>}
                  {doc.uploaded_at && <span>Uploaded {formatDate(doc.uploaded_at)}</span>}
                </div>
              </button>
            );
          })
        )}
      </div>
    </DialogContent>
  );
};

export default AttachDocumentDialog;
